import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import { ChartView } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { CostSavingChartData } from '../../types'

/**
 * Determines if additional data points are required to optimize visualization of the chart data.
 * Optimization is required if only one tick is displayed. In that case a line trace does not work and would be displayed as a dot instead.
 * Therefore, additional data is added to still show a proper line.
 *
 * One tick would be shown if
 * * the selected day is in the current week and it is Monday
 * * the selected day is in the current month and is the first day of the month
 * * the selected day is in the current year and its january
 *
 * #one-tick-visualization
 *
 * @param view
 * @param to
 * @returns
 */
export const requiresAdditionalDataPoints = (view: ChartView, to?: Moment): boolean => {
  if (view === 'day' || !to) return false

  const toIsCurrentDate = isCurrentDate(view, to)

  if (!toIsCurrentDate) return false

  const isMonday = to.clone().day() === 1
  const isFirstDayOfMonth = to.clone().date() === 1
  const isInJanuary = to.clone().month() === 0

  if (view === 'week') return isMonday
  if (view === 'month') return isFirstDayOfMonth
  if (view === 'year') return isInJanuary

  return false
}

/**
 * For visualization purposes one additional data point is fetched to the left/in the past of the current from date.
 * #one-tick-visualization
 */
export const updateFromToAddAdditionalDataPointToTheLeft = (fetchFrom: Moment, view: ChartView): Moment => {
  if (view === 'week' || view === 'month') {
    const previousDayFrom = fetchFrom.clone().subtract(1, 'day')
    return previousDayFrom
  }
  if (view === 'year') {
    const previousMonthFrom = fetchFrom.clone().subtract(1, 'month')
    return previousMonthFrom
  }
  return fetchFrom
}

/**
 * For visualization purposes one additional data point is added to the right of the graph with null values (since future data is not available).
 * This shifts the existing tick more to the left of the graph & this is used for one-tick-visualization.
 * #one-tick-visualization
 *
 * @param data
 * @param view
 * @param historyData
 * @returns
 */
export const addAdditionalDataPointToTheRight = (data: CostSavingChartData[], view: ChartView): CostSavingChartData[] => {
  const additionalDataPoint: CostSavingChartData = {
    time: moment(data?.[data.length - 1].time as string)
      ?.add(1, view === 'year' ? 'month' : 'day')
      .valueOf(),
    threeSixtyWallboxProducedEnergy: null,
    threeSixtyWallboxSavings: null,
    threeSixtyBatteryProducedEnergy: null,
    threeSixtyBatterySavings: null,
    consumptionPrice: null,
  }
  const updatedData = [...data, additionalDataPoint]

  return updatedData
}

/**
 * Check if the given date is in the current date or in the current month (based on the view).
 *
 * @param view
 * @param to
 * @returns
 */
const isCurrentDate = (view: ChartView, to: Moment): boolean => {
  if (view === 'week' || view === 'month') return to.clone().isSame(moment(), 'day')
  if (view === 'year') return to.clone().isSame(moment(), 'month')
  return false
}
